import {
  ContentFeatureModel,
  DisclaimerFeatureModel,
  FeatureModelUnion,
  IconlistFeatureModel,
  MediaFeatureModel,
  QsgSearchResult,
  SectionChainItem,
  SectionModel,
} from '../../fetch-data/graphql';
import { matchesQueryFuzzy } from './fuzzy';
import striptags from 'striptags';
import {
  determineResultTitle,
  extractFirstParagraph,
  findMatchingMarkdownTitle,
  trimParagraphContainingTerm,
} from './extract';
import { determineTitleForFeature } from './parse';

export function searchInFeatureTitles(
  feature: ContentFeatureModel,
  lowerSearchTerm: string,
  searchTerm: string,
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  const featureTitleText = feature.title?.plaintext || '';

  const matches = useFuzzy
    ? matchesQueryFuzzy(featureTitleText, searchTerm)
    : featureTitleText.toLowerCase().includes(lowerSearchTerm);

  if (!matches) return;

  let previewContent = '';
  let previewHtml: string | null = null;
  let previewPlaintext: string | null = null;

  if (feature.content?.[0]) {
    const firstContent = feature.content[0];
    previewContent = extractFirstParagraph(firstContent.plaintext || '');
    previewHtml = firstContent.html || null;
    previewPlaintext = firstContent.plaintext || null;
  }

  allResults.push({
    chain: newChain,
    sectionId: section.sectionId || '',
    sectionName: section.sectionName || '',
    sectionTitle: sectionChainItem.sectionTitle,
    resultTitle: {
      html: feature.title?.html || null,
      plaintext: feature.title?.plaintext?.trim() || '',
    },
    resultContent: {
      html: previewHtml,
      plaintext: previewPlaintext,
      trimmed: previewContent,
    },
  });
}

export function searchInMarkdownTitles(
  feature: ContentFeatureModel,
  searchTerm: string,
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  matchedContentIndices: Set<number>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  if (!feature.content) return;

  for (
    let contentIndex = 0;
    contentIndex < feature.content.length;
    contentIndex++
  ) {
    const contentItem = feature.content[contentIndex];
    if (!contentItem || !contentItem.plaintext || !contentItem.markdown)
      continue;

    // findMatchingMarkdownTitle now returns the BEST match (highest scoring)
    // It finds all matches and picks the one with exact phrase match or highest score
    const markdownMatch = findMatchingMarkdownTitle(
      contentItem.markdown,
      contentItem.plaintext,
      searchTerm,
      contentItem.html,
      useFuzzy
    );

    if (markdownMatch.title) {
      matchedContentIndices.add(contentIndex);

      allResults.push({
        chain: newChain,
        sectionId: section.sectionId || '',
        sectionName: section.sectionName || '',
        sectionTitle: sectionChainItem.sectionTitle,
        resultTitle: markdownMatch.title,
        resultContent: {
          html: contentItem.html || null,
          plaintext: contentItem.plaintext || null,
          trimmed: markdownMatch.paragraph || '',
        },
      });
    }
  }
}

export function searchInContentItems(
  feature: ContentFeatureModel,
  lowerSearchTerm: string,
  searchTerm: string,
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  matchedContentIndices: Set<number>,
  allResults: QsgSearchResult[],
  _useFuzzy: boolean = false
): void {
  if (!feature.content) return;

  for (
    let contentIndex = 0;
    contentIndex < feature.content.length;
    contentIndex++
  ) {
    // Skip if this content block was already matched via markdown title
    if (matchedContentIndices.has(contentIndex)) continue;

    const contentItem = feature.content[contentIndex];
    if (!contentItem || !contentItem.plaintext) continue;

    const plaintext = contentItem.plaintext;
    const lowerText = plaintext.toLowerCase();

    // Find ALL matches in this content
    const allMatchPositions: Array<{ index: number; isExactPhrase: boolean }> = [];

    // 1. Find ALL exact phrase matches first (these are the best matches)
    let exactIndex = 0;
    while ((exactIndex = lowerText.indexOf(lowerSearchTerm, exactIndex)) !== -1) {
      allMatchPositions.push({
        index: exactIndex,
        isExactPhrase: true,
      });
      exactIndex += lowerSearchTerm.length;
    }

    // 2. If no exact phrase matches, find word matches
    if (allMatchPositions.length === 0) {
      const searchWords = lowerSearchTerm.split(/\s+/).filter(w => w.length > 0);
      const wordPositions: Map<string, number[]> = new Map();
      
      // Find all occurrences of each word
      for (const word of searchWords) {
        const positions: number[] = [];
        let wordIndex = 0;
        while ((wordIndex = lowerText.indexOf(word, wordIndex)) !== -1) {
          positions.push(wordIndex);
          wordIndex += word.length;
        }
        if (positions.length > 0) {
          wordPositions.set(word, positions);
        }
      }

      // Group nearby words (within 100 chars) - this finds phrases like "Travel Assist System"
      const allWordPositions = Array.from(wordPositions.values()).flat().sort((a, b) => a - b);
      const processedIndices = new Set<number>();

      for (const pos of allWordPositions) {
        if (processedIndices.has(pos)) continue;

        const regionEnd = pos + 100;
        const wordsInRegion = new Set<string>();
        
        for (const [word, positions] of wordPositions.entries()) {
          if (positions.some(p => p >= pos && p <= regionEnd)) {
            wordsInRegion.add(word);
          }
        }

        // Only add if multiple words are found together (likely a phrase)
        if (wordsInRegion.size > 1 || searchWords.length === 1) {
          allMatchPositions.push({
            index: pos,
            isExactPhrase: false,
          });

          // Mark this region as processed
          for (let i = pos; i <= regionEnd && i < plaintext.length; i++) {
            processedIndices.add(i);
          }
        }
      }
    }

    // 3. Create a result for EACH match found - scoring will pick the best
    for (const matchPos of allMatchPositions) {
      const resultTitle = determineResultTitle(
        feature,
        section,
        contentItem,
        searchTerm
      );
      
      // Use the specific match position for trimming so each result has correct preview
      const trimmed = trimParagraphContainingTerm(
        plaintext,
        searchTerm,
        150,
        matchPos.index
      );

      allResults.push({
        chain: newChain,
        sectionId: section.sectionId || '',
        sectionName: section.sectionName || '',
        sectionTitle: sectionChainItem.sectionTitle,
        resultTitle,
        resultContent: {
          html: contentItem.html || null,
          plaintext: contentItem.plaintext || null,
          trimmed,
        },
      });
    }
  }
}

export function searchInDisclaimerFeatures(
  feature: DisclaimerFeatureModel,
  lowerSearchTerm: string,
  searchTerm: string,
  featureIndex: number,
  allFeatures: FeatureModelUnion[],
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  if (!feature.disclaimerContent || !feature.disclaimerContent.plaintext)
    return;

  const plaintext = feature.disclaimerContent.plaintext;

  // Use fuzzy or exact matching
  const matches = useFuzzy
    ? matchesQueryFuzzy(plaintext, searchTerm)
    : plaintext.toLowerCase().includes(lowerSearchTerm);

  if (!matches) return;

  const trimmed = trimParagraphContainingTerm(plaintext, searchTerm);
  const resultTitle = determineTitleForFeature(
    feature,
    featureIndex,
    allFeatures,
    section
  );

  allResults.push({
    chain: newChain,
    sectionId: section.sectionId || '',
    sectionName: section.sectionName || '',
    sectionTitle: sectionChainItem.sectionTitle,
    resultTitle,
    resultContent: {
      html: feature.disclaimerContent.html || null,
      plaintext: feature.disclaimerContent.plaintext || null,
      trimmed,
    },
  });
}

export function searchInIconListFeatures(
  feature: IconlistFeatureModel,
  lowerSearchTerm: string,
  searchTerm: string,
  featureIndex: number,
  allFeatures: FeatureModelUnion[],
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  if (!feature.items || feature.items.length === 0) return;

  for (const item of feature.items) {
    if (!item || !item.html) continue;

    // Extract plaintext from HTML for searching
    const plaintext = striptags(item.html).trim();

    // Use fuzzy or exact matching
    const matches = useFuzzy
      ? matchesQueryFuzzy(plaintext, searchTerm)
      : plaintext.toLowerCase().includes(lowerSearchTerm);

    if (!matches) continue;

    const trimmed = trimParagraphContainingTerm(plaintext, searchTerm);
    const resultTitle = determineTitleForFeature(
      feature,
      featureIndex,
      allFeatures,
      section
    );

    allResults.push({
      chain: newChain,
      sectionId: section.sectionId || '',
      sectionName: section.sectionName || '',
      sectionTitle: sectionChainItem.sectionTitle,
      resultTitle,
      resultContent: {
        html: item.html || null,
        plaintext: plaintext || null,
        trimmed,
      },
    });

    // Only add one result per icon list feature
    break;
  }
}

export function searchInMediaFeatures(
  feature: MediaFeatureModel,
  lowerSearchTerm: string,
  searchTerm: string,
  featureIndex: number,
  allFeatures: FeatureModelUnion[],
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  if (!feature.caption || !feature.caption.plaintext) return;

  const plaintext = feature.caption.plaintext;

  // Use fuzzy or exact matching
  const matches = useFuzzy
    ? matchesQueryFuzzy(plaintext, searchTerm)
    : plaintext.toLowerCase().includes(lowerSearchTerm);

  if (!matches) return;

  const trimmed = trimParagraphContainingTerm(plaintext, searchTerm);
  const resultTitle = determineTitleForFeature(
    feature,
    featureIndex,
    allFeatures,
    section
  );

  allResults.push({
    chain: newChain,
    sectionId: section.sectionId || '',
    sectionName: section.sectionName || '',
    sectionTitle: sectionChainItem.sectionTitle,
    resultTitle,
    resultContent: {
      html: feature.caption.html || null,
      plaintext: feature.caption.plaintext || null,
      trimmed,
    },
  });
}
